import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { jobsService } from '../services/jobs.service';
import { trainingService } from '../services/training.service';
import { algorithmsService } from '../services/algorithms.service';
import { cvService } from '../services/cv.service';
import { useNavigate } from 'react-router-dom';
import { AchievementsBar } from '../components/achievements/AchievementsBar';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function greeting(name?: string | null) {
  const hour = new Date().getHours();
  const time = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  return name ? `Good ${time}, ${name}.` : `Good ${time}.`;
}

function computeFocusArea(args: {
  cvScore: number | null | undefined;
  trainingPct: number;
  algoSolved: number;
  jobTotal: number;
  jobsInTech: number;
}) {
  const { cvScore, trainingPct, algoSolved, jobTotal, jobsInTech } = args;

  if (!cvScore || cvScore === 0) {
    return {
      desc: 'Upload your resume to get an AI-powered analysis and actionable improvements.',
    };
  }
  if (trainingPct < 25) {
    return {
      desc: 'Complete your first training modules to unlock interview preparation.',
    };
  }
  if (algoSolved === 0) {
    return {
      desc: 'Solve your first coding problem to strengthen your technical interviews.',
    };
  }
  if (jobTotal === 0) {
    return {
      desc: 'Start logging job applications to monitor your pipeline and conversion rate.',
    };
  }
  if (jobsInTech > 0) {
    return {
      desc: `You have ${jobsInTech} role${jobsInTech > 1 ? 's' : ''} in the technical stage. Practice with the AI interviewer.`,
    };
  }
  if (trainingPct >= 85 && algoSolved >= 5 && jobTotal >= 3) {
    return {
      desc: 'Strong progress across all areas. Book a mock interview with a mentor.',
    };
  }
  if (trainingPct < 85) {
    return {
      desc: `${Math.round(trainingPct)}% complete. Finish your training to unlock expert verification.`,
    };
  }
  return {
    desc: 'Apply to more roles and use the AI assistant to optimize your outreach.',
  };
}

function statusColor(status: string) {
  const colors: Record<string, string> = {
    APPLIED: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
    HR: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    TECH: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
    OFFER: 'bg-green-500/15 text-green-600 dark:text-green-400',
    REJECTED: 'bg-red-500/15 text-red-600 dark:text-red-400',
  };
  return colors[status] ?? 'bg-surface-container-highest text-on-surface-variant';
}

function statusIcon(status: string) {
  const icons: Record<string, string> = {
    APPLIED: 'send',
    HR: 'support_agent',
    TECH: 'code',
    OFFER: 'celebration',
    REJECTED: 'block',
  };
  return icons[status] ?? 'work';
}

function scoreColorClass(score: number) {
  if (score >= 8) return 'text-green-500';
  if (score >= 5) return 'text-amber-500';
  return 'text-red-500';
}

function scoreBgClass(score: number) {
  if (score >= 8) return 'bg-green-500';
  if (score >= 5) return 'bg-amber-500';
  return 'bg-red-500';
}

/* ─── Sub-components ──────────────────────────────────────────────────────── */

function QuickAction({
  icon,
  label,
  onClick,
  gradient,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  gradient: string;
}) {
  return (
    <button
      onClick={onClick}
      className="group relative flex min-w-[100px] max-w-[140px] flex-col items-center gap-3 rounded-2xl border border-outline-variant/10 bg-surface-container-lowest p-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-outline-variant/30 dark:bg-surface-container-high dark:hover:bg-surface-container-highest"
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-md transition-transform duration-300 group-hover:scale-110"
        style={{ background: gradient }}
      >
        <span translate="no" className="material-symbols-outlined text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
          {icon}
        </span>
      </div>
      <span className="text-xs font-bold text-on-surface text-center break-words">{label}</span>
    </button>
  );
}

function CircularProgress({
  percentage,
  size = 120,
  stroke = 10,
  color = 'rgb(var(--color-primary))',
}: {
  percentage: number;
  size?: number;
  stroke?: number;
  color?: string;
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * (percentage / 100);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="absolute -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgb(var(--color-surface-container-highest))"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${filled} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease-out' }}
        />
      </svg>
      <div className="text-center leading-tight">
        <span className="text-3xl font-extrabold text-on-surface">{percentage}</span>
        <span className="text-sm font-bold text-on-surface-variant">%</span>
      </div>
    </div>
  );
}

function ScoreRing({ score, max = 10, size = 100 }: { score: number; max?: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * (score / max);
  const color = score >= 8 ? '#22c55e' : score >= 5 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <div
        className="absolute inset-0 rounded-full opacity-20 blur-lg"
        style={{ backgroundColor: color }}
      />
      <svg className="absolute -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgb(var(--color-surface-container-highest))"
          strokeWidth={6}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={6}
          strokeDasharray={`${filled} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease-out' }}
        />
      </svg>
      <div className="text-center leading-none">
        <span className="text-3xl font-extrabold text-on-surface">{score}</span>
        <span className="text-xs font-bold text-on-surface-variant">/{max}</span>
      </div>
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-container-high/60 px-3 py-2">
      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs text-on-surface-variant">{label}</span>
      <span className="text-xs font-bold text-on-surface">{value}</span>
    </div>
  );
}

function DifficultyCard({
  label,
  value,
  total,
  color,
  icon,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
  icon: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-xl border border-outline-variant/10 bg-surface-container-high/40 p-3">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg text-white" style={{ backgroundColor: color }}>
          <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
        </div>
        <span className="text-xs font-semibold text-on-surface">{label}</span>
      </div>
      <div className="flex items-end gap-1">
        <span className="text-xl font-extrabold text-on-surface">{value}</span>
        <span className="text-[10px] text-on-surface-variant mb-1">/ {total}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container-highest">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function JobPipeline({ jobs }: { jobs: import('../services/jobs.service').Job[] }) {
  const stages = ['APPLIED', 'HR', 'TECH', 'OFFER', 'REJECTED'] as const;
  const labels = ['Applied', 'HR', 'Tech', 'Offer', 'Rejected'];
  const colors = [
    'rgb(var(--color-primary))',
    'rgb(var(--color-tertiary))',
    'rgb(var(--color-secondary))',
    'rgb(var(--color-success))',
    'rgb(var(--color-error))',
  ];
  const icons = ['send', 'support_agent', 'code', 'celebration', 'block'];
  const counts = stages.map((s) => jobs.filter((j) => j.status === s).length);
  const total = counts.reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-1 flex-col justify-center">
      <div className="grid grid-cols-5 gap-2">
        {counts.map((c, i) => {
          const pct = total > 0 ? (c / total) * 100 : 0;
          return (
            <div key={stages[i]} className="flex flex-col items-center gap-2">
              <div className="relative flex h-16 w-full items-end justify-center overflow-hidden rounded-xl bg-surface-container-high/50">
                <div
                  className="w-full rounded-t-xl transition-all duration-700"
                  style={{
                    height: `${Math.max(pct, c > 0 ? 8 : 4)}%`,
                    backgroundColor: colors[i],
                    opacity: 0.85,
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-lg ring-2 ring-white/20"
                    style={{ backgroundColor: colors[i] }}
                  >
                    <span
                      className="material-symbols-outlined text-base text-white"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      {icons[i]}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                  {labels[i]}
                </span>
                <span className="block text-sm font-extrabold text-on-surface">{c}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SuggestionCard({
  icon,
  title,
  subtitle,
  action,
  onClick,
  gradient,
}: {
  icon: string;
  title: string;
  subtitle: string;
  action: string;
  onClick: () => void;
  gradient: string;
}) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col gap-3 rounded-2xl border border-outline-variant/10 bg-surface-container-lowest p-4 text-left shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-outline-variant/25 dark:bg-surface-container-high"
    >
      <div className="flex items-start justify-between">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm"
          style={{ background: gradient }}
        >
          <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
            {icon}
          </span>
        </div>
        <span className="material-symbols-outlined text-lg text-on-surface-variant transition-colors group-hover:text-primary">
          arrow_forward
        </span>
      </div>
      <div>
        <p className="text-sm font-bold text-on-surface">{title}</p>
        <p className="mt-0.5 text-xs text-on-surface-variant line-clamp-2">{subtitle}</p>
      </div>
      <div className="mt-auto">
        <span
          className="inline-block rounded-lg px-3 py-1.5 text-[11px] font-bold text-white shadow-sm transition-transform group-hover:scale-105"
          style={{ background: gradient }}
        >
          {action}
        </span>
      </div>
    </button>
  );
}

/* ─── Main Dashboard ──────────────────────────────────────────────────────── */

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ['job-stats'],
    queryFn: () => jobsService.getStats(),
    initialData: { total: 0, interviews: 0, offers: 0, conversionRate: 0 },
  });

  const { data: progress } = useQuery({
    queryKey: ['training-progress'],
    queryFn: () => trainingService.getProgress(),
    initialData: { total: 0, completed: 0, percentage: 0, records: [] },
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => jobsService.findAll(),
  });

  const { data: algoStats } = useQuery({
    queryKey: ['algo-stats'],
    queryFn: () => algorithmsService.getStats(),
    initialData: {
      total: 0,
      solved: 0,
      attempted: 0,
      byDifficulty: { EASY: 0, MEDIUM: 0, HARD: 0 },
      totalAttempts: 0,
      successRate: 0,
    },
  });

  const { data: cvState } = useQuery({
    queryKey: ['cv-state'],
    queryFn: () => cvService.getState(),
  });

  const displayName = user?.name || (user?.email ? user.email.split('@')[0] : 'there');

  const jobsInTech = jobs.filter((j) => j.status === 'TECH').length;
  const cvScore = cvState?.cv?.score ?? null;

  const focus = computeFocusArea({
    cvScore,
    trainingPct: progress.percentage,
    algoSolved: algoStats.solved,
    jobTotal: stats.total,
    jobsInTech,
  });

  const recentJobs = jobs.slice(0, 3);

  const totalAlgoByDiff = algoStats.byDifficulty.EASY + algoStats.byDifficulty.MEDIUM + algoStats.byDifficulty.HARD;

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-4 md:space-y-10 md:p-8 lg:p-12">
      {/* ── Header ── */}
      <section className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-on-surface md:text-4xl">
            {greeting(displayName)}
          </h1>
          <p className="max-w-lg text-base text-on-surface-variant md:text-lg">{focus.desc}</p>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-3 md:items-end">
          <AchievementsBar />
        </div>
      </section>

      {/* ── Quick Actions ── */}
      <section data-tour="tour-quick-actions" className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
        <QuickAction
          icon="school"
          label="Training"
          onClick={() => navigate('/training')}
          gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
        />
        <QuickAction
          icon="code"
          label="Algorithms"
          onClick={() => navigate('/training/algorithms')}
          gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
        />
        <QuickAction
          icon="description"
          label="CV"
          onClick={() => navigate('/cv')}
          gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
        />
        <QuickAction
          icon="auto_awesome"
          label="AI Chat"
          onClick={() => navigate('/assistant')}
          gradient="linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
        />
        <QuickAction
          icon="ads_click"
          label="Job Tracker"
          onClick={() => navigate('/jobs')}
          gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
        />
      </section>

      {/* ── Bento Grid ── */}
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* Training Progress */}
        <div className="col-span-12 flex flex-col justify-between gap-4 rounded-2xl bg-surface-container-low p-6 lg:col-span-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>school</span>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Training Progress</p>
            </div>
            <button
              onClick={() => navigate('/training')}
              className="flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-bold text-white shadow-sm transition-all hover:shadow-md primary-gradient"
            >
              {progress.percentage >= 85 ? 'Verify' : progress.percentage > 0 ? 'Continue' : 'Start'}
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-5xl font-black text-on-surface">{progress.percentage}<span className="text-2xl text-on-surface-variant">%</span></span>
            <div className="flex gap-4">
              <div>
                <div className="text-lg font-bold text-on-surface">{progress.completed}</div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-on-surface-variant">Done</div>
              </div>
              <div className="h-8 w-px bg-outline-variant/20" />
              <div>
                <div className="text-lg font-bold text-on-surface">{Math.max(progress.total - progress.completed, 0)}</div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-on-surface-variant">Left</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              <span>Start</span>
              <span>Half</span>
              <span>Expert</span>
            </div>
            <div className="flex gap-1.5">
              {[33, 66, 100].map((m) => (
                <div key={m} className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-container-highest">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-700"
                    style={{ width: `${Math.min(Math.max((progress.percentage / m) * 100, 0), 100)}%` }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-surface-container-high/60 p-3">
              <div className="text-lg font-extrabold text-on-surface">{progress.completed}</div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-on-surface-variant">Completed</div>
            </div>
            <div className="rounded-xl bg-surface-container-high/60 p-3">
              <div className="text-lg font-extrabold text-on-surface">{Math.max(progress.total - progress.completed, 0)}</div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-on-surface-variant">Remaining</div>
            </div>
            <div className="rounded-xl bg-surface-container-high/60 p-3">
              <div className="text-lg font-extrabold text-primary">
                {progress.records.filter((r) => {
                  if (!r.completedAt) return false;
                  const days = (Date.now() - new Date(r.completedAt).getTime()) / (1000 * 60 * 60 * 24);
                  return days <= 7;
                }).length}
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-on-surface-variant">This week</div>
            </div>
          </div>
        </div>

        {/* CV Score */}
        <div className="col-span-12 flex flex-col rounded-2xl bg-surface-container-low p-5 md:p-7 sm:col-span-6 lg:col-span-6">
          <div className="mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-tertiary text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
              description
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-tertiary">CV Score</p>
          </div>

          {cvScore ? (
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex items-center gap-5">
                <ScoreRing score={cvScore} />
                <div className="flex-1 space-y-2">
                  <div className={`text-2xl font-extrabold ${scoreColorClass(cvScore)}`}>
                    {cvScore >= 8 ? 'Strong' : cvScore >= 5 ? 'Good' : 'Needs Work'}
                  </div>
                  <p className="text-xs text-on-surface-variant">
                    {cvScore >= 8
                      ? 'Minor tweaks can make it exceptional.'
                      : cvScore >= 5
                        ? 'Address flagged issues to improve ATS performance.'
                        : 'Several critical issues detected. Prioritize the red flags.'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <StatPill label="ATS" value={cvScore >= 7 ? 90 : cvScore >= 5 ? 70 : 45} color={cvScore >= 7 ? '#22c55e' : cvScore >= 5 ? '#f59e0b' : '#ef4444'} />
                <StatPill label="Format" value={cvScore >= 6 ? 85 : 60} color={cvScore >= 6 ? '#22c55e' : '#f59e0b'} />
                <StatPill label="Content" value={cvScore >= 8 ? 95 : cvScore >= 5 ? 75 : 50} color={cvScore >= 8 ? '#22c55e' : cvScore >= 5 ? '#f59e0b' : '#ef4444'} />
              </div>
              <button
                onClick={() => navigate('/cv')}
                className="mt-auto flex w-full items-center justify-center gap-1.5 rounded-xl bg-tertiary/15 py-2.5 text-xs font-bold text-tertiary transition-all hover:bg-tertiary/25"
              >
                Improve your CV
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-outline-variant/30 py-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-tertiary/15">
                <span className="material-symbols-outlined text-3xl text-tertiary">upload_file</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-on-surface">No CV uploaded yet</p>
                <p className="text-xs text-on-surface-variant">Get an AI score and actionable feedback.</p>
              </div>
              <button
                onClick={() => navigate('/cv')}
                className="flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-xs font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
              >
                Analyze CV
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          )}
        </div>

        {/* Job Pipeline */}
        <div className="col-span-12 flex flex-col rounded-2xl bg-surface-container-low p-5 md:p-7 sm:col-span-6 lg:col-span-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-secondary text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
                ads_click
              </span>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-secondary">Job Pipeline</p>
            </div>
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10"
            >
              View all <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-outline-variant/30 py-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary/15">
                <span className="material-symbols-outlined text-2xl text-secondary">work_outline</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-on-surface">No applications tracked</p>
                <p className="text-xs text-on-surface-variant">Start monitoring your job search progress.</p>
              </div>
              <button
                onClick={() => navigate('/jobs')}
                className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold text-white shadow-sm transition-all hover:shadow-md hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                Track first job
                <span className="material-symbols-outlined text-sm">add</span>
              </button>
            </div>
          ) : (
            <>
              <JobPipeline jobs={jobs} />
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-surface-container-high/50 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-on-surface">{stats.total}</div>
                  <div className="text-[10px] font-medium text-on-surface-variant uppercase tracking-wide">Total</div>
                </div>
                <div className="rounded-xl bg-surface-container-high/50 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-secondary">{stats.interviews}</div>
                  <div className="text-[10px] font-medium text-on-surface-variant uppercase tracking-wide">Interviews</div>
                </div>
                <div className="rounded-xl bg-surface-container-high/50 p-2.5 text-center">
                  <div className="text-lg font-extrabold text-green-400">{stats.offers}</div>
                  <div className="text-[10px] font-medium text-on-surface-variant uppercase tracking-wide">Offers</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Algorithm Practice */}
        <div className="col-span-12 flex flex-col justify-between rounded-2xl bg-surface-container-low p-5 md:p-7 lg:col-span-6">
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
                  code
                </span>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Algorithm Practice</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 rounded-lg px-2.5 py-1">
                  <span className="material-symbols-outlined text-xs text-amber-500">local_fire_department</span>
                  <span className="text-xs font-bold text-on-surface">{algoStats.solved > 0 ? '1 day' : '0 days'}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <DifficultyCard
                label="Easy"
                value={algoStats.byDifficulty.EASY}
                total={algoStats.total}
                color="#22c55e"
                icon="check_circle"
              />
              <DifficultyCard
                label="Medium"
                value={algoStats.byDifficulty.MEDIUM}
                total={algoStats.total}
                color="#f59e0b"
                icon="adjust"
              />
              <DifficultyCard
                label="Hard"
                value={algoStats.byDifficulty.HARD}
                total={algoStats.total}
                color="#ef4444"
                icon="bolt"
              />
            </div>

            <div className="mt-4 flex items-center gap-5 rounded-xl bg-surface-container-high/40 p-4">
              <div className="flex-1">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-on-surface">Success Rate</span>
                  <span className="rounded-md px-2 py-0.5 text-sm font-bold text-primary">{algoStats.successRate}%</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-container-highest">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-700"
                    style={{ width: `${algoStats.successRate}%` }}
                  />
                </div>
              </div>
              <div className="flex min-w-[64px] flex-col items-center rounded-xl bg-surface-container-high px-3 py-2">
                <span className="text-xl font-extrabold text-on-surface">{algoStats.solved}</span>
                <span className="text-[10px] font-medium text-on-surface-variant uppercase">Solved</span>
              </div>
            </div>
          </div>

          <div className="mt-5 flex gap-2">
            <button
              onClick={() => navigate('/training/algorithms')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-surface-container-high py-2.5 text-xs font-bold text-on-surface transition-all hover:bg-surface-container-highest"
            >
              Browse problems
            </button>
            <button
              onClick={() => navigate('/training/algorithms')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}
            >
              {algoStats.solved > 0 ? 'Solve next' : 'Start coding'}
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Recent Activity ── */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        <div className="rounded-2xl bg-surface-container-low p-5 md:p-7">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-secondary text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
                bolt
              </span>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-secondary">Recent Activity</p>
            </div>
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10"
            >
              All jobs <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
          {recentJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-outline-variant/30 py-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary/15">
                <span className="material-symbols-outlined text-2xl text-secondary">history</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-on-surface">No activity yet</p>
                <p className="text-xs text-on-surface-variant">Your recent job applications will appear here.</p>
              </div>
              <button
                onClick={() => navigate('/jobs')}
                className="flex items-center gap-1.5 rounded-xl bg-surface-container-high px-4 py-2 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-highest"
              >
                Track a job
                <span className="material-symbols-outlined text-sm">add</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentJobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  className="group flex w-full items-center gap-3 rounded-xl border border-outline-variant/5 bg-surface-container-high/50 p-3 text-left transition-all hover:border-outline-variant/15 hover:bg-surface-container-high"
                >
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `rgb(var(--color-${job.status === 'OFFER' ? 'success' : job.status === 'REJECTED' ? 'error' : 'primary'}-container) / 0.5)` }}
                  >
                    <span className="material-symbols-outlined text-lg text-primary">{statusIcon(job.status)}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-on-surface">
                      {job.role} <span className="font-normal text-on-surface-variant">at</span> {job.company}
                    </p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusColor(job.status)}`}>
                        {job.status}
                      </span>
                      <span className="text-[10px] text-on-surface-variant">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <span className="material-symbols-outlined shrink-0 text-sm text-on-surface-variant transition-colors group-hover:text-primary">
                    chevron_right
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
